/* eslint-disable max-lines */
import axios from "axios";
import CreateSubject from "@/components/CreateSubject/CreateSubject.vue";
import AllSubjects from "@/components/GetSubjects/SubjectComponent.vue";
import Multiselect from "vue-multiselect";

function get_selected_map_keys(map = new Map()) {
  return Array.from(map.entries())
    .filter(([, selected]) => selected)
    .map(([id]) => id);
}

function get_report_file_details(report) {
  const first_file = report.report_files?.[0];

  return {
    pdf_file_url: first_file?.file_url || "",
    pdf_file: first_file || null,
    file_name: first_file?.name || "",
  };
}

function add_unique_reports(target_reports, next_reports = []) {
  next_reports.forEach((new_report) => {
    if (!target_reports.some((report) => report.id === new_report.id)) {
      target_reports.push(new_report);
    }
  });
}

function get_grouped_tag_categories(tag_list = []) {
  const grouped = new Map();

  tag_list.forEach((tag) => {
    const category_name = tag.tag_category.name;
    const category_id = tag.tag_category.id;

    if (!grouped.has(category_name)) {
      grouped.set(category_name, { id: category_id, tags: [] });
    }

    grouped.get(category_name).tags.unshift({
      id: tag.id,
      name: tag.name,
    });
  });

  return grouped;
}

function get_home_category_items(grouped) {
  return Array.from(grouped.entries()).map(([name, { id, tags }]) => ({
    key: name.toLowerCase().replace(/\s+/g, "-"),
    id,
    name,
    tags,
  }));
}

function get_report_tag_categories(report, categories = []) {
  const primary_tag_id = report?.primary_tag?.id || null;

  const report_tag_ids = new Set([
    ...(report?.tags || []).map((tag) => tag.id),
    primary_tag_id
  ].filter(Boolean));

  return categories
    .map((category) =>
      get_report_category_tags(category, report_tag_ids, primary_tag_id)
    )
    .filter((category) => category.tags.length);
}

function get_report_category_tags(category, report_tag_ids, primary_tag_id) {
  const primary_tags = [];
  const secondary_tags = [];

  category.tags.forEach((tag) => {
    if (tag.id === primary_tag_id) {
      primary_tags.push({ ...tag, is_primary: true });
    } else if (report_tag_ids.has(tag.id)) {
      secondary_tags.push({ ...tag, is_primary: false });
    }
  });

  return {
    name: category.name,
    tags: [...primary_tags, ...secondary_tags],
  };
}

function apply_primary_tag_selection(context, category_id, tag) {
  const old_primary = context.primary_tags.get(category_id);
  let secondary_tags = context.selected_tags.get(category_id);

  if (tag) secondary_tags = secondary_tags.filter((item) => item.id !== tag.id);
  if (old_primary) secondary_tags = secondary_tags.filter((item) => item.id !== old_primary.id);

  context.primary_tags.clear();
  context.primary_tags.set(category_id, tag);
  context.primary_tag = tag;
  context.selected_tags.set(category_id, secondary_tags);
  context.primary_tags = new Map(context.primary_tags);
  context.selected_tags = new Map(context.selected_tags);
}

function get_selected_tag_ids(selected_tags = new Map()) {
  const tag_ids = [];
  selected_tags.forEach((tags_array) => {
    tags_array.forEach((tag) => {
      tag_ids.push(tag.id);
    });
  });

  return tag_ids;
}

function apply_report_tag_to_selection(context, report, tag) {
  const category_id = tag.tag_category.id;
  if (!context.selected_tags.has(category_id)) context.selected_tags.set(category_id, []);

  const category_tags = context.selected_tags.get(category_id);
  const next_tag = { id: tag.id, name: tag.name };

  if (report.primary_tag && tag.id === report.primary_tag.id) {
    category_tags.unshift(next_tag);
  } else {
    category_tags.push(next_tag);
  }

  context.selected_tags.set(category_id, category_tags);
}

export function get_all_selected_home_tags(context) {
  const tags = [];
  context.selected_tags.forEach((items) => {
    tags.push(...items);
  });

  if (!context.primary_tag) return tags;

  const primary_id = context.primary_tag.id;
  return [
    { id: context.primary_tag.id, name: context.primary_tag.name },
    ...tags.filter((tag) => tag.id !== primary_id),
  ];
}

export function get_filtered_home_reports(context) {
  const active_tags = get_selected_map_keys(context.selectedFilters);
  if (active_tags.length > 0) return context.reportsByTags;

  const active_categories = get_selected_map_keys(context.selectedCategories);
  return active_categories.length > 0
    ? context.reportsByCategories
    : context.reports;
}

export const homeTagsCategoryMethods = {
  isCategorySelected(_subjectId, categoryId) {
    return this.selectedCategories.get(String(categoryId)) === true;
  },

  toggleCategory(_subjectId, categoryId) {
    const safe_id = String(categoryId);
    const current = this.selectedCategories.get(safe_id) === true;

    this.selectedCategories.set(safe_id, !current);
    this.page = 1;
    this.has_more = true;
    this.reportsByTags = [];
    this.fetchReportsByCategory();
  },

  groupedTagsById(subjectTags) {
    const groups = new Map();

    subjectTags.forEach((subject_tag) => {
      if (!subject_tag.tag?.tag_category) return;

      const category_id = subject_tag.tag.tag_category.id;
      if (!groups.has(category_id)) groups.set(category_id, []);

      groups.get(category_id).push(subject_tag);
    });

    return groups;
  },

  async fetchReportsByTags() {
    if (this.is_loading || !this.has_more) return;
    this.is_loading = true;

    try {
      const res = await axios.post(
        `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_reports_by_tags/`,
        {
          page: this.page,
          page_size: this.page_size,
          tag_ids: get_selected_map_keys(this.selectedFilters),
        },
        { headers: { Authorization: `Token ${this.getToken()}` } }
      );

      this.apply_reports_by_tags_response(res.data);
    } catch (err) {
      console.error("Error fetching reports by tags:", err);
    } finally {
      this.finish_report_fetch();
    }
  },

  apply_reports_by_tags_response(data) {
    const { results, has_more, page } = data;
    this.prepare_report_files(results);
    this.add_unique_tag_reports(results);
    this.page = page + 1;
    this.has_more = has_more;
  },

  prepare_report_files(results = []) {
    results.forEach((report) => {
      this.prepare_report_file(report);
    });
  },

  prepare_report_file(report) {
    Object.assign(report, get_report_file_details(report));
  },

  add_unique_tag_reports(results = []) {
    add_unique_reports(this.reportsByTags, results);
  },

  add_unique_category_reports(results = []) {
    add_unique_reports(this.reportsByCategories, results);
  },

  finish_report_fetch() {
    this.is_loading = false;
    if (!this.pendingReportReload) return;

    this.pendingReportReload = false;
    this.fetchReports();
  },

  async fetchReportsByCategory() {
    if (this.is_loading || !this.has_more) return;
    this.is_loading = true;

    try {
      const res = await axios.post(
        `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_reports_by_category/`,
        {
          page: this.page,
          page_size: this.page_size,
          category_ids: get_selected_map_keys(this.selectedCategories),
        },
        { headers: { Authorization: `Token ${this.getToken()}` } }
      );

      this.apply_reports_by_category_response(res.data);
    } catch (err) {
      console.error("Error fetching reports by category:", err);
    } finally {
      this.finish_report_fetch();
    }
  },

  apply_reports_by_category_response(data) {
    const { results, has_more, page } = data;
    this.prepare_report_files(results);
    this.add_unique_category_reports(results);
    this.page = page + 1;
    this.has_more = has_more;
  },

  toggleTag(tagId) {
    const safe_id = String(tagId);

    this.selectedFilters.forEach((_value, key) => {
      this.selectedFilters.set(key, false);
    });

    this.selectedFilters.set(safe_id, true);
    this.page = 1;
    this.has_more = true;
    this.reportsByTags = [];
    this.fetchReportsByTags();
  },

  isTagSelected(tagId) {
    return this.selectedFilters.get(String(tagId)) === true;
  },

  filteredCategoriesForReport(report) {
    return get_report_tag_categories(report, this.categories);
  },

  fetch_grouped_tags() {
    this.isLoading = true;

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_all_tags/`,
      method: "GET",
      headers: {
        Authorization: `Token ${this.getToken()}`,
        "Content-Type": "application/json",
      },
      success: (response) => {
        if (response.status === "success") {
          this.categories = get_home_category_items(
            get_grouped_tag_categories(response.data.tags)
          );
        }
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  },

  openEditCategoryModal(report) {
    $("#edit_report_modal").modal("hide");

    if (report?.primary_tag) {
      const category_id = report.primary_tag.tag_category.id;
      this.primary_tags.set(category_id, report.primary_tag);
    }

    $("#category_modal_edit").modal("show");
  },

  updateTagSelection(categoryId, selectedTags) {
    this.selected_tags.set(categoryId, selectedTags);
  },

  submit_tags() {
    const all_selected = Array.from(this.selected_tags.values()).flat();

    this.primary_tags.forEach((tag) => {
      if (!all_selected.find((item) => item.id === tag.id)) {
        all_selected.push({ id: tag.id, name: tag.name });
      }
    });

    this.selected_categories = all_selected;
  },

  selectPrimaryTag(categoryId, tag) {
    apply_primary_tag_selection(this, categoryId, tag);
  },

  reset_report_tag_selection() {
    this.primary_tags = new Map();
    this.selected_tags = new Map();
    this.primary_tag = null;
  },

  apply_edit_report_tag_selection(report) {
    this.selected_categories = report.tags?.map((tag) => tag.tag_category) || [];
    this.reset_report_tag_selection();

    if (report.primary_tag) {
      const category_id = report.primary_tag.tag_category.id;
      this.primary_tags.set(category_id, report.primary_tag);
      this.primary_tags = new Map(this.primary_tags);
      this.primary_tag = report.primary_tag;
    }

    if (Array.isArray(report.tags)) {
      report.tags.forEach((tag) => {
        apply_report_tag_to_selection(this, report, tag);
      });
    }
  },

  append_report_tag_form_data(formData) {
    get_selected_tag_ids(this.selected_tags).forEach((id) => {
      formData.append("tag_ids", id);
    });

    if (this.primary_tag) {
      formData.append("primary_tag_id", this.primary_tag.id);
    }
  },
};

export default {
  components: {
    CreateSubject,
    AllSubjects,
    Multiselect,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    subjects: {
      type: Array,
      default: () => [],
    },
    clientFeatures: {
      type: Object,
      default: () => ({}),
    },
    selectedFilters: {
      type: Object,
      default: () => new Map(),
    },
    selectedCategories: {
      type: Object,
      default: () => new Map(),
    },
    selectedTags: {
      type: Object,
      default: () => new Map(),
    },
    primaryTags: {
      type: Object,
      default: () => new Map(),
    },
    allSelectedTags: {
      type: Array,
      default: () => [],
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    showEditor: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "new-subject",
    "open-edit-category-modal",
    "select-primary-tag",
    "submit-tags",
    "toggle-category",
    "toggle-tag",
    "update-tag-selection",
  ],
  data() {
    return {
      showAllPrimary: false,
      showAllSecondary: false,
    };
  },
  methods: {
    isTagSelected(tag_id) {
      return this.selectedFilters?.get(String(tag_id)) === true;
    },
    isCategorySelected(category_id) {
      return this.selectedCategories?.get(String(category_id)) === true;
    },
    groupedTagsById(subject_tags) {
      const groups = new Map();

      subject_tags.forEach((subject_tag) => {
        if (!subject_tag.tag?.tag_category) return;

        const category_id = subject_tag.tag.tag_category.id;
        if (!groups.has(category_id)) {
          groups.set(category_id, []);
        }
        groups.get(category_id).push(subject_tag);
      });

      return groups;
    },
  },
};
